/*
 * Copyright (c)  2020 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {ApplicationContext} from '../context/ApplicationContext';
import {get} from '../environment/environment';
import {info} from '../logger/logger';
// [ION-CORE TODO]: remove the following line when endpoint is ready
import {doorSensorHistoryResponse, sensorResponse} from '../tests/loadSensorMockData';
import {
  CrashboxxRecord,
  Entity,
  GenericObject,
  User,
  Users
} from '../types/entities';
import {
  BulkOperationResults,
  Service,
  UserFilter
} from '../types/services';
import {simulatedBulkOperation} from './Connect';
import {
  aggregateResults,
  client,
  entityId,
  normalizeDates,
  parse,
  parseCollection,
  partitionBulkUpdates,
  url
} from './ConnectUtils';

export enum UserContext {
  suite = ApplicationContext.ion,
  ctcAdmin = ApplicationContext.admin,
  assistcargo = ApplicationContext.assistcargo,
  ayvens = ApplicationContext.ayvens,
  racconnect = ApplicationContext.racconnect,
  brunoschillig = ApplicationContext.brunoschillig
};

/**
 * @function getUserContext
 *
 * Reverse convert ApplicationContext to UserContext. This seems unnecessary, but will
 * be removed as part of a larger cleanup by merging ApplicationContext & UserContext
 *
 * ```typescript
 *
 *    import {getUserContext} from '@ion/core/src/services/MicroServices';
 *    import {getApplicationContext} from '@ion/core/src/environment/environment';
 *
 *    const userContext = getUserContext(getApplicationContext());
 *
 * ```
 */
export const getUserContext = (ctx: ApplicationContext): UserContext => {
  switch (ctx) {
    case ApplicationContext.admin:
      return UserContext.ctcAdmin;
    case ApplicationContext.assistcargo:
      return UserContext.assistcargo;
    case ApplicationContext.racconnect:
      return UserContext.racconnect;
    case ApplicationContext.brunoschillig:
      return UserContext.brunoschillig;
    case ApplicationContext.ayvens:
      return UserContext.ayvens;
    case ApplicationContext.ion:
    default:
      return UserContext.suite;
  }
};

/**
 * @function getAlertStatus
 *
 * Given a Triggered Alert UUID, retrieves the status of the Alert Actions (Email, SMS)
 * and returns the result.
 *
 * ```typescript
 *
 *    import {getAlertStatus} from '@ion/core/src/services/MicroServices';
 *
 *    const status = await getAlertStatus(alert);
 *
 * ```
 */
export const getAlertStatus = async(uuid: string): Promise<any> => {
  const response = await client.get(url(`/notify/${uuid}/status?verbose=true`, Service.iOnMicroServices));
  return parseCollection(response, 'notificationStatus');
};

/**
 * Given a Triggered Alert UUIDs, retrieves the status of the Alert Actions (Email, SMS)
 * and returns the result.
 *
 * ```typescript
 *
 *    import {getAlertsStatus} from '@ion/core/src/services/MicroServices';
 *
 *    const status = await getAlertStatus([id1, id2,..., idn]);
 *
 * ```
 */
export const getAlertsStatus = async(uuids: string[]): Promise<any> => {
  const results = await Promise.allSettled(uuids.map(async(uuid) => await getAlertStatus(uuid)));
  return results.map(({value}: any) => value);
};

/**
 * Retrieves the inheritable group properties of the ancestor Group hierarchy
 * for the given group Id.
 *
 * The service response is returned in the following form:
 *
 * ```typescript
 *
 *   {
 *     response: {
 *       property: {
 *         value: <value>,
 *         source: {
 *           id: <account-id>,
 *           name: <account-name>
 *         }
 *      }
 *    }
 *
 * ```
 *
 * @method getInheritedProperties
 * @param  {number} groupId The id of the group for which inherited properties
 *         are to be retrieved.
 */
export const getInheritedProperties = async(groupId: number): Promise<any> => {
  info(`MicroServices getInheritedProperties: groupId ${groupId}`);
  const endpoint = url(`/accounts/${groupId}/inheritableProperties`, Service.iOnMicroServices);
  const response = await client.get(endpoint);
  return response?.data?.response;
};

/**
 * @function createUser
 *
 * When a new User is to be created in CTC, the process of creation can be delegated to the
 * MicroService in order to both create the user and send an email confirmation to the user.
 *
 * ```typescript
 *
 *    import {createUser} from '@ion/core/src/services/MicroServices';
 *
 *    // create user in ctc and send user a confirmation email ...
 *    const status = await createUser(user);
 *
 * ```
 *
 * @param {User} user the CTC User entity which is to be created.
 * @param {UserContext} context An optional context which, when provided, will be added
 *         as a query string parameter to inform the MicroServices of the context
 *         (e.g. '?suite=true').
 */
export const createUser = async(user: User, context?: UserContext, options?: UserContext): Promise<User> => {
  info(`MicroServices createUser: username ${user.username}`);
  let endpoint = url('/user', Service.iOnMicroServices);
  endpoint += getQueryParams(user, context, options);
  const response = await client.post(endpoint, JSON.stringify({user}));
  return parse(response, 'user');
};

/**
 *
 * When a new User is to be created in CTC, the process of creation can be delegated to the
 * MicroService in order to both create the user and send an email confirmation to the user.
 *
 * ```typescript
 *
 *    import {createBulkUsers} from '@ion/core/src/services/MicroServices';
 *
 *    // create user in ctc and send user a confirmation email ...
 *    const status = await createBulkUsers(user);
 *
 * ```
 *
 */
export const createBulkUsers = async(users: User): Promise<User> => {
  const endpoint = url('/users/bulk', Service.ApiBaseUrl);
  const response = await client.post(endpoint, JSON.stringify({accountId: users.accountId, users}));
  const result = response?.data?.response;
  return result;
};

/**
 * @function updateUser
 *
 * When an existing User is to be update in CTC, the process of updating can be delegated to
 * the MicroService in order to update the user and send an email notification to the user if
 * necessary.
 *
 * ```typescript
 *
 *    import {updateUser} from '@ion/core/src/services/MicroServices';
 *
 *    // update user in ctc and send a user notification if needed ...
 *    const status = await createUser(user);
 *
 * ```
 *
 * @param {User} user the CTC User entity which is to be updated.
 * @param {UserContext} context An optional context which, when provided, will be added
 *         as a query string parameter to inform the MicroServices of the context
 *         (e.g. '?suite=true').
 */
export const updateUser = async(user: User, context?: UserContext, options?: UserContext): Promise<User> => {
  info(`MicroServices updateUser: username ${user.username}`);
  let endpoint = (context === UserContext.ctcAdmin || options === UserContext.ctcAdmin) ? url(`/users/${user?.id}`, Service.Connect)
    : url('/user', Service.iOnMicroServices);
  endpoint += getQueryParams(user, context, options);
  const response = await client.put(endpoint, JSON.stringify({user}));
  return parse(response, 'user');
};

/**
 * @function updateUsers
 *
 * The updateUsers function updates existing Users Entity within the CTC Platform.
 *
 * ```typescript
 *
 *     import {updateUsers} from '@ion/core/src/services/Connect';
 *
 *     const {results, errors} = await updateUsers(users);
 *
 * ```
 *
 * @param   {Users} users The CTC Users entity which are to be updated.
 * @returns {Promise<Users>} The updated Users entity.
 */
export const updateUsers = async(
  users: Users, context?: UserContext): Promise<BulkOperationResults> => {
  return simulatedBulkOperation({items: users, api: updateUser, context});
};

/**
 * The createUsers function creates a collection of new User Entities within the
 * CTC Platform.
 *
 * ```typescript
 *
 *     import {createUsers} from '@ion/core/src/services/Connect';
 *
 *     const {results, errors} = await createUsers(users);
 *
 * ```
 *
 * @param   users The CTC User Entities which are to be created.
 * @returns The created User Entities
 */
export const createUsers = async(
  users: Users, context?: UserContext): Promise<BulkOperationResults> => {
  return simulatedBulkOperation({items: users, api: createUser, context});
};

/**
 * Performs an User search based on the provided UserSearchableProperty and
 * maxResults (defaults to 100).
 *
 * ```typescript
 *    import {searchUsers} from '@ion/core/src/services/Connect';
 *
 *    const users = await searchUsers({username: '*.ion'}, 10);
 *
 * ```
 */
export const searchUsers = async(
  searchTerms: UserFilter,
  maxResults = 100
): Promise<any> => {
  const response = await client.post(url('/users/search'), JSON.stringify({
    search : {
      searchTerms : normalizeDates(searchTerms),
      maxResults
    }
  }));
  return parseCollection(response, 'user');
};

/**
 * The bulkUserSearch method provides a CTC Search against a list of User
 * entities by username.
 *
 * @param {array} users An array of User entities which are to be located by
 *        username.
 */
export const bulkUserSearch = async(users: Users, includeAccount = true): Promise<Users> => {
  const userIds = users?.map(user => user?.id).toString();
  const searchTerms: UserFilter = {
    ...(userIds.length !== 0 && {
      id : userIds
    })
  };
  if (includeAccount) {
    const account = users?.length ? users[0].account : {};
    searchTerms.account = entityId(account);
  }
  let response;
  if (!isEmpty(searchTerms)) {
    response = await searchUsers(searchTerms, 10000);
  } else {
    response = [];
  }
  return response;
};

/**
 * The bulkSearchAndUpdateUsers method provides a composite method which
 * performs a CTC Search, Bulk Update, and Bulk Add (if needed) against a
 * list of User entities. The list of entities is iterated and segmented
 * into seperate lists based on the account to which the users reside.
 *
 * @param {array} users An array of User entities which are to be updated and
 *        / or created.
 */
export const bulkSearchAndUpdateUsers = async(user: User): Promise<Users> => {
  const groupMap = {};
  const groupId = entityId(user.account);
  groupMap[groupId] = groupMap[groupId] || [];
  groupMap[groupId].push(user);
  const response = await bulkUserSearch(groupMap[groupId], false);
  return response;
};

/**
 * The bulkUpdateUsers method updates a list of existing Users Entities within
 * the CTC Platform for the given environment. This method faciliates the ability
 * to provide Bulk User Editing functionality.
 *
 * @param users An array of User entities which are to be updated.
 */
export const bulkUpdateUsers = async(
  users: Users, context?: UserContext): Promise<BulkOperationResults> => {
  const responses = await simulatedBulkOperation({items: users, api: bulkSearchAndUpdateUsers, context});
  let results    = {errors: [], results: []};
  const partitioned = partitionBulkUpdates({
    uploaded : users,
    searched : [].concat(...responses.results),
    entity   : Entity.user,
    key      : 'id'
  });
  const {adds, updates} = partitioned;
  let createUsersResponse: BulkOperationResults;
  let updateUsersResponse: BulkOperationResults;

  const resolvedAddUsers = [];
  const resolvedUpdateUsers = [];

  adds?.forEach(({user}) => {
    resolvedAddUsers.push(user);
  });

  updates.forEach(({user}) => {
    resolvedUpdateUsers.push(user);
  });

  if (adds.length) {
    createUsersResponse = await createUsers(resolvedAddUsers, context);
    results = aggregateResults(results, createUsersResponse);
  }

  if (updates.length) {
    updateUsersResponse = await updateUsers(resolvedUpdateUsers, context);
    return aggregateResults(results, updateUsersResponse);
  }
  return results;
};

/**
 * Returns crash data for a given time range that is provided in the searchData
 *
 * ```typescript
 *
 *    import {loadCrashboxxData} from '@ion/core/src/services/MicroServices';
 *
 * ```
 */
export const loadCrashboxxData = async({
  searchData,
  page,
  pageSize,
  sort = '-crashDate',
  includeSubGroups
}): Promise<any> => {
  const uri      = `/icn/search?pg=${page}&pgsize=${pageSize}&sort=${sort}${includeSubGroups ? '&hierarchy=true' : ''}`;
  const response: {
    config: GenericObject
    data: {
      response: {
        results: CrashboxxRecord[];
        totalCount: number;
      }
    }
  } = await client.post(url(uri, Service.Crashboxx), JSON.stringify(searchData));
  return parseCollection(response, 'icn');
};

/**
 * Gets sensor data for a given time query from the smarter trailer sensor service
 *
 * ```typescript
 *
 *    import {getSensorData} from '@ion/core/src/services/MicroServices';
 *
 * ```
 */
export const getSensorData = async({
  mobileId,
  apps = '',
  numberOfRecords = 1,
  mobileIdType = 'ESN'
}: {
  mobileId: string;
  apps?: string;
  numberOfRecords?: number
  mobileIdType?: string;
}): Promise<any> => {
  try {
    const {data: {response: {results}}} = await client({
      url    : url('/search', Service.SensorsService),
      method : 'POST',
      data   : {
        search : {
          sort        : ['-messageTime'],
          maxResults  : numberOfRecords,
          searchTerms : {
            mobileIdType,
            mobileId,
            ...(apps && {sensorType: apps})
          }
        }
      }
    });
    return results;
  } catch (e) {
    return {};
  }
};

// [ION-CORE TODO]: remove the following function when endpoint is ready;
// callers should then invoke getSensorData() with appropriate params...
export const getMockedDoorSensorHistory = async(): Promise<any> => {
  return new Promise(resolve => {
    const {response: {results}} = doorSensorHistoryResponse;
    setTimeout(() => {
      resolve(results);
    }, 1000);
  });
};

/**
 * Provides a utility to create query param string based on user entity & context params.
 */
const getQueryParams = (user: User, context?: UserContext, options?: UserContext): String => {
  const queryParams = [];
  let queryString = '';
  if (context === UserContext.ctcAdmin || options === UserContext.ctcAdmin) {
    queryParams.push(`notify=${user?.notify ? user.notify : false}`);
  }
  if (context) {
    queryParams.push(`${context}=true`);
  }
  if (options) {
    queryParams.push(`${options}=true`);
  }
  const {isSsoUser} = user;
  if (!isNil(isSsoUser)) {
    queryParams.push(`sso=${isSsoUser}`);
  }
  queryParams.push(`oidcEnabled=${get('oidcEnabled')}`);
  queryParams.forEach((val, index) => {
    queryString += ((index === 0) ? '?' : '&');
    queryString += val;
  });
  return queryString;
};

/**
 * Private functions exported for Jest.
 */
export const testFunctions = {
  testGetQueryParams : getQueryParams
};
